import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Container, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import Breadcrumb from 'components/App/Breadcrumb';
import Loading from 'components/App/Loading';
import Meta from 'components/App/Meta';
import AppSelectValidation from 'components/App/SelectValidation';
import giftApi, { USER_GIFT_STATUS } from 'api/giftApi';
import BcoinIcon from 'assets/images/Bcoin.svg';
import IconUserExchange from 'assets/images/icon-user-exchange.svg';
import CopyIcon from 'assets/images/copy.svg';
import ENDPOINT from 'constants/endPoint';
import { handleError, showToastError } from 'utils';

import styles from './ManagerGiftExchangeDetail.module.scss';

const PAGE_TITLE = 'Chi tiết đơn đổi quà';

const GIFT_CATEGORY_LABEL = {
  LIMITED: 'Quà giới hạn',
  EXCLUSIVE: 'Quà Độc Quyền',
  VOUCHER: 'Quà Voucher',
};

const STATUS_SELECT_OPTIONS = [
  { value: USER_GIFT_STATUS.PENDING, label: 'Chờ gửi quà' },
  { value: USER_GIFT_STATUS.UNUSED, label: 'Chưa sử dụng' },
  { value: USER_GIFT_STATUS.DELIVERED, label: 'Đã gửi quà' },
  { value: USER_GIFT_STATUS.RECEIVED, label: 'Đã nhận' },
  { value: USER_GIFT_STATUS.USED, label: 'Đã sử dụng' },
  { value: USER_GIFT_STATUS.EXPIRED, label: 'Lỗi' },
];

const EXCLUSIVE_STATUS_OPTIONS = [
  { value: USER_GIFT_STATUS.PENDING, label: 'Chờ gửi quà' },
  { value: USER_GIFT_STATUS.DELIVERED, label: 'Đã gửi quà' },
];

const DELIVERY_METHOD = {
  EMAIL: 'EMAIL',
  BOOKESE_SYSTEM: 'BOOKESE_SYSTEM',
  EXPRESS: 'EXPRESS',
};

const DELIVERY_METHOD_OPTIONS = [
  {
    value: DELIVERY_METHOD.EMAIL,
    label: 'Gửi qua Email',
  },
  {
    value: DELIVERY_METHOD.BOOKESE_SYSTEM,
    label: 'Nhận trên hệ thống Bookese',
  },
  {
    value: DELIVERY_METHOD.EXPRESS,
    label: 'Giao hàng',
  },
];

const isExclusiveGift = (gift) => gift?.category === 'EXCLUSIVE';

const DETAIL_ROWS = [
  { key: 'transactionId', label: 'Mã giao dịch', copyable: false },
  { key: 'fullName', label: 'Người nhận', copyable: true },
  { key: 'email', label: 'Email', copyable: true },
  { key: 'phoneNumber', label: 'Số điện thoại', copyable: true },
  { key: 'address', label: 'Địa chỉ nhận', copyable: true },
];

const formatTransactionId = (id) => (id ? `#${id}` : '—');

const formatGiftPeriod = (gift) => {
  if (!gift?.startAt || !gift?.endAt) {
    return GIFT_CATEGORY_LABEL[gift?.category] || gift?.category || '';
  }

  const categoryLabel = GIFT_CATEGORY_LABEL[gift.category] || gift.category;
  const start = dayjs(gift.startAt).format('D/M/YYYY');
  const end = dayjs(gift.endAt).format('D/M/YYYY');

  return `${categoryLabel} ${start} - ${end}`;
};

const formatCoinValue = (value) => new Intl.NumberFormat('vi-VN').format(value ?? 0);

const getStatusLabel = (status, options) => (
  options.find((option) => option.value === status)?.label || '—'
);

function CopyButton({ value }) {
  const onCopy = useCallback(async () => {
    if (!value) return;

    try {
      await navigator.clipboard.writeText(String(value));
      toast.success('Đã sao chép');
    } catch {
      showToastError('Không thể sao chép');
    }
  }, [value]);

  if (!value) return null;

  return (
    <button
      type="button"
      className={styles.CopyButton}
      onClick={onCopy}
      aria-label="Sao chép"
    >
      <img src={CopyIcon} alt="" width={20} height={20} />
    </button>
  );
}

CopyButton.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

CopyButton.defaultProps = {
  value: '',
};

function ManagerGiftExchangeDetail() {
  const { id } = useParams();
  const history = useHistory();

  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const breadcrumbList = useMemo(() => [
    {
      name: 'Quà tặng',
      isActive: false,
      link: ENDPOINT.MANAGER_GIFT_LIST,
    },
    {
      name: 'Danh sách đổi quà',
      isActive: false,
      link: ENDPOINT.MANAGER_GIFT_EXCHANGE,
    },
    {
      name: PAGE_TITLE,
      isActive: true,
      link: '',
    },
  ], []);

  const fetchDetails = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const response = await giftApi.getUserGiftDetails(id);
      const data = response?.payload ?? response;

      if (data && (data._id || data.id)) {
        setDetails(data);
      } else {
        setDetails(null);
      }
    } catch (error) {
      handleError(error);
      setDetails(null);
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetails();
  }, [fetchDetails]);

  const onChangeStatus = useCallback(async (event) => {
    const nextStatus = event.target.value;

    if (isExclusiveGift(details?.gift)) {
      if (details.status !== USER_GIFT_STATUS.PENDING) return;
      if (nextStatus !== USER_GIFT_STATUS.DELIVERED) return;
    }

    try {
      setIsUpdatingStatus(true);
      await giftApi.updateUserGiftStatus(id, { status: nextStatus });
      toast.success('Cập nhật trạng thái thành công');
      await fetchDetails();
    } catch (error) {
      handleError(error);
    } finally {
      setIsUpdatingStatus(false);
    }
  }, [details, fetchDetails, id]);

  const statusSelectOptions = useMemo(() => {
    if (!details) return STATUS_SELECT_OPTIONS;

    if (isExclusiveGift(details.gift)) {
      return EXCLUSIVE_STATUS_OPTIONS;
    }

    return STATUS_SELECT_OPTIONS;
  }, [details]);

  const isStatusSelectDisabled = useMemo(() => {
    if (!details || isUpdatingStatus) return true;

    if (isExclusiveGift(details.gift)) {
      return details.status !== USER_GIFT_STATUS.PENDING;
    }

    return false;
  }, [details, isUpdatingStatus]);

  const rowValues = useMemo(() => {
    if (!details) return {};

    return {
      transactionId: formatTransactionId(details.userGiftId),
      fullName: details.fullName || details.user?.fullName || '—',
      email: details.user?.email || '—',
      phoneNumber: details.phoneNumber || details.user?.phoneNumber || details.user?.phone || '—',
      address: details.address || '—',
    };
  }, [details]);

  const giftImage = details?.gift?.images?.[0] || details?.gift?.previewImage;
  const coinValue = details?.coin ?? details?.gift?.coin ?? 0;

  if (!isLoading && !details) {
    return (
      <div className={`page-content ${styles.Page}`}>
        <Meta name={PAGE_TITLE} />
        <Container fluid>
          <div className={styles.PageInner}>
            <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} />
            <p className="text-center text-danger mb-0">Không tìm thấy đơn đổi quà</p>
            <button
              type="button"
              className="btn btn-link mt-3"
              onClick={() => history.push(ENDPOINT.MANAGER_GIFT_EXCHANGE)}
            >
              Quay lại danh sách
            </button>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className={`page-content ${styles.Page}`}>
      <Meta name={PAGE_TITLE} />
      {/* <Breadcrumb pageTitle={PAGE_TITLE} breadcrumbList={breadcrumbList} /> */}
      <h3 className={styles.Title}>chi tiết đơn quà</h3>

      <Container fluid>
        <div className={styles.PageInner}>

          <Loading loading={isLoading} loaderOnPage />

          {details && (
            <div className={styles.Content}>
              <section className={styles.SummaryCard}>
                <div className={styles.SummaryMain}>
                  <div className={styles.GiftImageWrap}>
                    {giftImage ? (
                      <img
                        src={giftImage}
                        alt={details.gift?.name}
                        className={styles.GiftImage}
                      />
                    ) : (
                      <div className={styles.GiftImagePlaceholder} />
                    )}
                  </div>

                  <div className={styles.GiftInfo}>
                    <h2 className={styles.GiftName}>
                      {details.gift?.name || '—'}
                    </h2>
                    <p className={styles.GiftPeriod}>
                      {formatGiftPeriod(details.gift)}
                    </p>
                    <div className={styles.BcoinRow}>
                      <img src={BcoinIcon} alt="Bcoin" width={16} height={16} />
                      <span className={styles.BcoinValue}>
                        {formatCoinValue(coinValue)}
                        {' '}
                        Bcoin
                      </span>
                    </div>
                  </div>
                </div>

                {isExclusiveGift(details.gift) && (
                <div className={styles.StatusWrap}>
                  {isStatusSelectDisabled ? (
                    <span className={styles.SummaryBadge}>
                      {getStatusLabel(details.status, statusSelectOptions)}
                    </span>
                  ) : (
                    <AppSelectValidation
                      name="status"
                      key="status"
                      options={statusSelectOptions}
                      value={details.status || ''}
                      onChange={(option) => onChangeStatus({ target: { value: option?.value ?? '' } })}
                      isDisabled={isStatusSelectDisabled}
                      isShowError={false}
                      selectedClassName={styles.StatusSelect}
                    />
                  )}
                  {isUpdatingStatus && (
                  <Spinner size="sm" color="primary" className={styles.StatusSpinner} />
                  )}
                </div>
                )}

                {details?.gift?.category && String(details?.gift?.category).trim().toUpperCase() === 'VOUCHER' && (
                <div className={styles.StatusWrap}>
                  <span className={styles.SummaryBadge}>
                    {DELIVERY_METHOD_OPTIONS.find(
                      (opt) => opt.value === (details.deliveryMethod || details.gift?.deliveryMethod || '').toUpperCase(),
                    )?.label || '—'}
                  </span>
                </div>
                )}
              </section>

              <section className={styles.DetailsCard}>
                <div className={styles.DetailsHeader}>
                  <span className={styles.DetailsHeaderIcon} aria-hidden>
                    <img
                      src={IconUserExchange}
                      alt=""
                      width={21}
                      height={21}
                    />
                  </span>
                  <h3 className={styles.DetailsTitle}>Thông tin đơn đổi quà</h3>
                </div>

                <div className={styles.DetailsList}>
                  {DETAIL_ROWS.map((row, index) => (
                    <div
                      key={row.key}
                      className={`${styles.DetailRow} ${index % 2 === 0 ? styles.DetailRowAlt : ''}`}
                    >
                      <span className={styles.DetailLabel}>
                        {row.label}
                      </span>
                      <div className={styles.DetailValueWrap}>
                        <span className={styles.DetailValue}>
                          {rowValues[row.key]}
                        </span>
                        {row.copyable && (
                        <CopyButton value={rowValues[row.key] !== '—' ? rowValues[row.key] : ''} />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <div className={styles.line}> </div>
              </section>
            </div>
          )}
        </div>
      </Container>
    </div>
  );
}

export default ManagerGiftExchangeDetail;
